import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { Instagram, Twitter, Linkedin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "@/data/images";
import apiClient from "@/lib/api";

export const Footer = () => {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await apiClient.post("/newsletter/subscribe", { email, lang });
      toast.success(t("footer.newsletter_success"));
      setEmail("");
    } catch {
      toast.error(t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const cols = [
    {
      title: t("footer.product"),
      links: [
        { to: `/${lang}/features`, label: t("nav.features") },
        { to: `/${lang}/pricing`, label: t("nav.pricing") },
        { to: `/${lang}/pro`, label: t("nav.pro") },
      ],
    },
    {
      title: t("footer.company"),
      links: [
        { to: `/${lang}/about`, label: t("nav.about") },
        { to: `/${lang}/press`, label: t("nav.press") },
        { to: `/${lang}/testimonials`, label: t("nav.testimonials") },
        { to: `/${lang}/contact`, label: t("nav.contact") },
      ],
    },
    {
      title: t("footer.resources"),
      links: [
        { to: `/${lang}/blog`, label: t("nav.blog") },
        { to: `/${lang}/faq`, label: t("nav.faq") },
      ],
    },
    {
      title: t("footer.legal"),
      links: [
        { to: `/${lang}/legal/mentions`, label: t("footer.mentions") },
        { to: `/${lang}/legal/cgu`, label: t("footer.cgu") },
        { to: `/${lang}/legal/cookies`, label: t("footer.cookies") },
      ],
    },
  ];

  return (
    <footer data-testid="site-footer" className="bg-slate900 text-white pt-20 pb-10 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(30,58,138,0.4),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_2fr] gap-12 pb-14 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img src={IMAGES.logo} alt="Fastority" className="h-8 w-8 object-contain" />
              <span className="font-heading font-extrabold text-xl">Fastority</span>
            </div>
            <p className="text-white/60 text-sm max-w-xs leading-relaxed">{t("footer.tagline")}</p>
            <div className="flex gap-3 mt-6">
              <a href="#" aria-label="Instagram" data-testid="social-instagram" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-coral transition-colors">
                <Instagram size={16} />
              </a>
              <a href="#" aria-label="Twitter" data-testid="social-twitter" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-coral transition-colors">
                <Twitter size={16} />
              </a>
              <a href="#" aria-label="LinkedIn" data-testid="social-linkedin" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-coral transition-colors">
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            {cols.map((col) => (
              <div key={col.title}>
                <h4 className="text-xs uppercase tracking-[0.15em] font-bold text-white/40 mb-4">{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} data-testid={`footer-link-${l.label}`} className="text-sm text-white/75 hover:text-white transition-colors">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-10">
          <div>
            <h4 className="font-heading font-bold text-lg mb-1">{t("footer.newsletter_title")}</h4>
            <p className="text-white/60 text-sm">{t("footer.newsletter_text")}</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full md:w-auto gap-2" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("footer.newsletter_placeholder")}
              data-testid="newsletter-email-input"
              className="flex-1 md:w-64 bg-white/10 border border-white/15 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-coral"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-button"
              className="bg-coral text-white rounded-full px-5 py-3 flex items-center gap-1.5 text-sm font-semibold hover:bg-white hover:text-slate900 transition-colors disabled:opacity-60"
            >
              {t("footer.newsletter_cta")} <ArrowRight size={15} />
            </button>
          </form>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 text-center text-white/40 text-xs">
          Fastority © {new Date().getFullYear()}. {t("footer.rights")}
        </div>
      </div>
    </footer>
  );
};
